import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';

export default function ConfigDisplay({ config, onRemove }) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(config.string);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getBadgeStyles = (type) => {
    switch (type) {
      case 'vless': return 'bg-purple-900/50 text-purple-400';
      case 'vmess': return 'bg-blue-900/50 text-blue-400';
      case 'reality': return 'bg-emerald-900/50 text-emerald-400';
      case 'hiddify': return 'bg-cyan-900/50 text-cyan-400 border border-cyan-500/30';
      case 'subscription': return 'bg-amber-900/50 text-amber-400 border border-amber-500/30';
      default: return 'bg-gray-800 text-gray-400';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900/80 rounded-3xl border border-white/5 overflow-hidden shadow-2xl backdrop-blur-xl mb-4"
    >
      <div className="flex justify-between items-center px-5 py-4 bg-white/[0.02] border-b border-white/5">
        <div className="flex items-center space-x-3">
          <span className={`px-2.5 py-1 text-[9px] font-black rounded-lg uppercase tracking-widest ${getBadgeStyles(config.type)}`}>
            {config.type}
          </span>
          <span className="font-bold text-xs text-white/90 truncate max-w-[150px] md:max-w-none tracking-tight">
            {config.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowQr(!showQr)} 
            className={`p-2 rounded-xl transition-all ${showQr ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20' : 'text-gray-500 hover:text-white bg-white/5'}`}
            title="Toggle QR Code"
          >
            <SafeIcon icon={FiIcons.FiMaximize} size={16} />
          </button>
          <button 
            onClick={onRemove} 
            className="p-2 text-gray-500 hover:text-red-400 transition-colors bg-white/5 rounded-xl"
          >
            <SafeIcon icon={FiIcons.FiTrash2} size={16} />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <AnimatePresence mode="wait">
          {showQr ? (
            <motion.div
              key="qr"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col items-center justify-center py-4 bg-white rounded-2xl"
            >
              <QRCodeSVG
                value={config.string}
                size={180}
                level="H"
                includeMargin={true}
                imageSettings={{
                  src: "https://www.cloudflare.com/img/logo-cloudflare-dark.svg",
                  x: undefined,
                  y: undefined,
                  height: 24,
                  width: 24,
                  excavate: true,
                }}
              />
              <p className="mt-4 text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                Scan with {config.type === 'subscription' ? 'H-APP / Hiddify' : 'Proxy Client'}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative group"
            >
              <textarea
                readOnly
                value={config.string}
                className="w-full bg-black/40 text-blue-400 font-mono text-[11px] p-4 pr-12 rounded-2xl border border-white/5 h-28 resize-none outline-none break-all leading-relaxed"
              />
              <button
                onClick={handleCopy}
                className={`absolute top-3 right-3 p-2.5 rounded-xl flex items-center justify-center transition-all ${
                  copied 
                    ? 'bg-green-500 text-white shadow-lg shadow-green-500/20' 
                    : 'bg-white/10 text-white/50 hover:bg-white/20 hover:text-white'
                }`}
              >
                <SafeIcon icon={copied ? FiIcons.FiCheck : FiIcons.FiCopy} size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {config.type === 'subscription' && (
          <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <SafeIcon icon={FiIcons.FiLink} className="text-amber-400" size={14} />
            <p className="text-[10px] text-amber-200/70 font-medium leading-tight">
              Paste this URL into your app's "Subscriptions" section.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}